import { useSchedulingContext } from 'context/SchedulingContext/SchedulingProvider'
import { memo, useEffect, useState } from 'react'
import BookScreen from './BookScreen'
import ConfirmScreen from './ConfirmScreen'
import LocationScreen from './LocationScreen'
import SuccessScreen from './SuccessScreen'
import AppointmentTypeScreen from './AppointmentTypeScreen'
import { AppointmentBookStartEventProps } from 'utils/amplitude.types'
import { Amplitude } from 'utils/amplitude.utils'
import useSearchParams from 'hooks/useSearchParams'
import { User } from '@allara-health/source-health-client'

const SchedulingRouter: React.FC = () => {
  const {
    amplitudeBookingMetadata,
    appointment,
    appointmentType,
    currentScreen,
    setCurrentScreen,
    dateTime,
    timeZone,
    rebookingFlowData,
    setRebookingFlowData,
    isReschedule,
  } = useSchedulingContext()
  const [
    hasAmplitudeAppointmentBookStartEventBeenSent,
    setHasAmplitudeAppointmentBookStartEventBeenSent,
  ] = useState<boolean>(false)
  const [daysSinceNearestSlot, setDaysSinceNearestSlot] = useState<number>(0)
  const [
    firstAvailabilityForBookingSession,
    setFirstAvailabilityForBookingSession,
  ] = useState<string | null>(null)
  const searchParams = useSearchParams()

  /*
  When navigating into the scheduling flow where the source is reschedule,
  we want to show the book screen immediately.
  */
  useEffect(() => {
    if (searchParams.get('source') === 'reschedule') {
      setCurrentScreen('book')
    }
  }, [])

  useEffect(() => {
    /*
    we just want to log the first time the user enters the booking flow if they're booking a net-new appointment.
    This handling is designed so that we don't re-fire the event when the user clicks 'back'
    somewhere within the booking flow (to edit previous selections), so that we don't re-fire the event when the user clicks
    'Modify Appointment' (which takes them to the 'success' screen), and generally so that it's not spammy.

    for reschedules, we send this same event when the user clicks the 'Reschedule' button on the 'success' screen.
    */

    if (
      currentScreen !== 'success' &&
      appointmentType &&
      !hasAmplitudeAppointmentBookStartEventBeenSent
    ) {
      const amplitudeEventProps: AppointmentBookStartEventProps = {
        appointment_id: appointment?.id ?? null,
        is_reschedule: isReschedule ?? false,
        appointment_type_name: appointmentType?.name ?? '',
        ...amplitudeBookingMetadata,
      }
      Amplitude.appointmentBookStart(amplitudeEventProps)
      setHasAmplitudeAppointmentBookStartEventBeenSent(true)
    }
  }, [
    appointment,
    appointmentType,
    hasAmplitudeAppointmentBookStartEventBeenSent,
  ])

  /*
  A little hacky, but:
  If the user just canceled an appointment and they're on the book screen, they should always have
  rebooking flow data.
  */
  useEffect(() => {
    if (
      !appointment ||
      !appointmentType ||
      appointment.status !== 'canceled' ||
      rebookingFlowData ||
      appointment.participants.length < 1 ||
      !dateTime ||
      !timeZone ||
      currentScreen !== 'book'
    ) {
      return
    }

    const provider = appointment.participants[0].participant as User

    setRebookingFlowData({
      provider: provider,
      appointmentType: appointmentType,
      dateTime: dateTime,
      timeZone,
    })
  }, [appointmentType, appointment, currentScreen])

  switch (currentScreen) {
    case 'appointmentTypeSelect':
      return <AppointmentTypeScreen />
    case 'location':
      return <LocationScreen />
    case 'book':
      return (
        <BookScreen
          setDaysSinceNearestSlot={setDaysSinceNearestSlot}
          firstAvailabilityForBookingSession={
            firstAvailabilityForBookingSession
          }
          setFirstAvailabilityForBookingSession={
            setFirstAvailabilityForBookingSession
          }
        />
      )
    case 'confirm':
      return <ConfirmScreen daysSinceNearestSlot={daysSinceNearestSlot} />
    case 'success':
      return <SuccessScreen />
    default:
      return <LocationScreen />
  }
}

export default memo(SchedulingRouter)
